import { Component, OnInit, Inject, Optional } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { FormEventService } from './form-event.service';
import { EventsService } from '../crud-service';

interface SelectedField {
  name: string;
  label: string;
  type: string;
  required: boolean;
}

@Component({
  selector: 'app-form-event',
  templateUrl: './form-event.component.html',
  styleUrls: ['./form-event.component.sass'],
})
export class FormEventComponent implements OnInit {
  formId: string | null = null;
  responseForm: FormGroup;
  selectedFields: SelectedField[] = [];
  checkboxFields: SelectedField[] = [];
  isLoading: boolean = true;
  newQuestions: string[] = [];
  formImage: string | null = 'assets/img/wazir/wazir-icon.svg';

  constructor(
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private eventsService: EventsService,
    private formEventService: FormEventService
  ) {
    this.responseForm = this.fb.group({});
  }

  ngOnInit(): void {
    if (this.data && this.data.element) {
      this.loadEventData(this.data.element);
    } else {
      this.formId = this.route.snapshot.paramMap.get('id');
      if (this.formId) {
        this.eventsService.getEventById(this.formId).subscribe({
          next: (eventData) => this.loadEventData(eventData),
          error: (error) => console.error('Erro ao carregar evento:', error),
        });
      }
    }
  }

  private loadEventData(eventData: any): void {
    this.isLoading = false;
    this.formImage = eventData.image || 'assets/img/wazir/wazir-icon.svg';

    console.log('Dados originais do evento carregados:', JSON.stringify(eventData, null, 2));

    // Normalizando valores para garantir consistência
    Object.keys(eventData).forEach((key) => {
      if (eventData[key] === 'SIM' || eventData[key] === 'TRUE' || eventData[key] === true) {
        eventData[key] = true;
      } else if (eventData[key] === 'NÃO' || eventData[key] === 'FALSE' || eventData[key] === false) {
        eventData[key] = false;
      }
    });

    console.log('Dados do evento normalizados:', JSON.stringify(eventData, null, 2));
    this.processEventFields(eventData);
  }

  private processEventFields(eventData: any): void {
    // Processando campos de checkbox (assuntos)
    const checkboxFieldMapping: { [key: string]: string } = {
      financial: 'Financeiro',
      legal: 'Jurídico',
      consultant: 'Consultor',
      partner: 'Parceiro',
      representative: 'Representante',
      accounting: 'Contábil',
      solutions: 'Soluções',
      plans: 'Planos',
      dashboard: 'Painel de Controle',
      support: 'Suporte',
    };

    // Filtra campos marcados como true
    this.checkboxFields = Object.keys(checkboxFieldMapping)
      .filter((key) => {
        const value = eventData[key];
        return value === true;
      })
      .map((key) => ({
        name: key,
        label: checkboxFieldMapping[key],
        type: 'checkbox',
        required: false,
      }));

    console.log('Checkboxes selecionados:', this.checkboxFields);

    // Mapeando campos de entrada
    const inputFieldMapping: { [key: string]: string } = {
      name: 'Nome',
      enterprise: 'Empresa',
      telephone: 'Telefone',
      instagram: 'Instagram',
      email: 'E-mail',
      question: 'Pergunta',
    };

    // Campos obrigatórios e campos marcados como SIM (true)
    this.selectedFields = Object.keys(inputFieldMapping)
      .filter((key) => {
        // Nome e email são sempre incluídos
        if (key === 'name' || key === 'email') {
          return true;
        }

        // Para os outros campos, verifica se estão marcados como SIM/true
        const value = eventData[key];
        const shouldInclude = value === true || value === 'SIM';
        console.log(`Campo ${key}: ${value} => incluir: ${shouldInclude}`);
        return shouldInclude;
      })
      .map((key) => ({
        name: key,
        label: inputFieldMapping[key],
        type: 'text',
        required: key === 'name' || key === 'email', // Nome e email são obrigatórios
      }));

    console.log('Campos de input selecionados:', this.selectedFields);

    // Processando perguntas personalizadas
    if (eventData.customQuestions && Array.isArray(eventData.customQuestions) && eventData.customQuestions.length > 0) {
      this.newQuestions = eventData.customQuestions
        .map((question: any) => {
          if (typeof question === 'object' && question !== null) {
            return question.question || '';
          }
          return question;
        })
        .filter((question: string) => question && question !== '');

      console.log('Perguntas personalizadas processadas:', this.newQuestions);
    } else {
      console.log('Nenhuma pergunta personalizada encontrada');
    }

    this.buildForm(eventData);
  }

  private buildForm(eventData: any): void {
    const controls: { [key: string]: any } = {};

    // Configurando campos de checkbox
    this.checkboxFields.forEach((field) => {
      controls[field.name] = [false]; // Inicia desmarcado para o usuário selecionar
    });

    // Configurando campos selecionados (incluindo os marcados como SIM)
    this.selectedFields.forEach((field) => {
      controls[field.name] = [''];
    });

    // Configurando campos para perguntas personalizadas
    this.newQuestions.forEach((question, index) => {
      controls[`customQuestion${index}`] = [''];
    });

    // Configurando campos para perguntas extras (limite definido)
    const limitValue = parseInt(eventData.limit, 10) || 0;
    const shouldShowExtraQuestions =
      (eventData.question === true || eventData.question === 'SIM') &&
      limitValue > 0;

    const extraQuestionsControls = this.fb.array([]);
    if (shouldShowExtraQuestions) {
      console.log(`Adicionando ${limitValue} perguntas extras ao formulário`);
      for (let i = 0; i < limitValue; i++) {
        extraQuestionsControls.push(this.fb.control(''));
      }
    }
    controls['extraQuestions'] = extraQuestionsControls;

    // Outros campos fixos (ex.: campos de reunião)
    const systemId =
      typeof eventData.system === 'object' && eventData.system?.id
        ? eventData.system.id
        : eventData.systemId || null;

    controls['system'] = [{ value: systemId, disabled: true }];
    controls['meetingDateTime'] = [{ value: eventData.meetingDateTime || '', disabled: true }];
    controls['typeQuestion'] = [{ value: eventData.typeQuestion || '', disabled: true }];
    controls['linkMeeting'] = [{ value: eventData.linkMeeting || '', disabled: true }];

    this.responseForm = this.fb.group(controls);
    console.log('Formulário construído:', {
      selectedFields: this.selectedFields.length,
      checkboxFields: this.checkboxFields.length,
      newQuestions: this.newQuestions.length,
      extraQuestions: extraQuestionsControls.length
    });
  }

  get extraQuestions(): FormArray {
    return this.responseForm.get('extraQuestions') as FormArray;
  }

  submitForm(): void {
    if (this.responseForm.valid) {
      const formData = {
        eventFormId: this.formId,
        name: this.responseForm.get('name')?.value || '',
        enterprise: this.responseForm.get('enterprise')?.value || '',
        telephone: this.responseForm.get('telephone')?.value || '',
        instagram: this.responseForm.get('instagram')?.value || '',
        email: this.responseForm.get('email')?.value || '',
        answers: [
          ...this.selectedFields.map((field) => ({
            question: field.label,
            answer: this.responseForm.get(field.name)?.value || '',
          })),
          ...this.checkboxFields.map((field) => ({
            question: field.label,
            answer: this.responseForm.get(field.name)?.value || false,
          })),
          ...this.newQuestions.map((question, index) => ({
            question: question,
            answer: this.responseForm.get(`customQuestion${index}`)?.value || '',
          })),
          ...this.extraQuestions.controls.map((control, index) => ({
            question: `Pergunta ${index + 1}`,
            answer: control.value || '',
          })),
        ],
      };

      console.log('📤 Enviando respostas para o backend:', formData);

      this.formEventService.saveFormResponses(formData).subscribe({
        next: (response) => {
          console.log('Respostas salvas com sucesso:', response);
          alert('Respostas enviadas com sucesso!');
          this.responseForm.reset();
        },
        error: (err) => {
          console.error('Erro ao salvar respostas:', err);
          alert('Erro ao salvar respostas. Tente novamente.');
        },
      });
    } else {
      alert('Por favor, preencha todos os campos obrigatórios.');
    }
  }
}
